import { detect } from 'detect-browser';

/**
 * Determines if WebP images can be used
 * @param {object} browser browser object
 * @returns {boolean} whether or not the browser supports webP
 */
function useWebP(browser) {
  switch (browser && browser.name) {
  case 'android':
  case 'chrome':
    return true;
  default:
    return false;
  }
}

/**
 * Getter for viewport width
 * @returns {int} viewport width
 */
export const getViewport = () => {
  if (window && window.visualViewport && window.visualViewport.width) {
    return window.visualViewport.width;
  }
  if (window && window.innerWidth) {
    return window.innerWidth;
  }
  return false;
};

/**
 * Generate path to webp version of image
 * @param {string} path basic image path
 * @param {int} viewportWidth width of viewport
 * @param {num} widthMultiplier fraction of screen width that the image takes up, defaults to 1
 * @param {num} height optional, specifies height of image
 * @returns {string} webp image path
 */
export const generateImageUrl = (path, viewportWidth, widthMultiplier = 1) => {
  const browser = detect();
  let fileExtension = 'png';
  if (useWebP(browser)) {
    fileExtension = 'webp';
  }
  if (browser && browser.os === 'iOS') {
    fileExtension = 'png';
  }
  const size = Math.ceil(viewportWidth * widthMultiplier);
  if (path) {
    return `${path}?fm=${fileExtension}&w=${size}`;
  }
  return '';
};

/**
 * Generate path to webp version of image
 * @param {string} path basic image path
 * @param {int} height width of viewport
 * @param {num} width fraction of screen width that the image takes up, defaults to 1
 * @param {number} ratio device pixel ration
 * @returns {string} webp image path
 */
export const generateFixedSizeUrl = ({ img, dimensions, ratio = 1 }) => {
  const browser = detect();
  let fileExtension = 'png';
  if (useWebP(browser)) {
    fileExtension = 'webp';
  }
  if (browser && browser.os === 'ios') {
    fileExtension = 'png';
  }
  if (img && img.path) {
    const dWidth = dimensions.width * ratio;
    const dHeight = dimensions.height * ratio;

    if (img.width > img.height) {
      return `${img.path}?fm=${fileExtension}&w=${dHeight * Math.round(img.width / img.height)}&h=${dHeight}`;
    }

    return `${img.path}?fm=${fileExtension}&w=${dWidth}&h=${dWidth * Math.round(img.height / img.width)}`;
  }
  return '';
};

/**
 * Get resolutions to pass to gatsby-image component
 * @param {object} data parameter data
 * @returns {object} resolutions object
 */
export const getResolutions = ({ image = {}, viewportWidth, height }) => {
  const {
    file = {
      url: '',
      details: {
        image: {
          width: 0,
          height: 0,
        },
      },
    },
    resolutions = {
      base64: '',
    },
  } = image;
  const {
    url = '',
    details = {
      image: {
        width: 0,
        height: 0,
      },
    },
  } = file;
  const {
    image: dimensions = {
      width: 0,
      height: 0,
    },
  } = details;
  const { base64 = '' } = resolutions;
  let params = `w=${viewportWidth}&q=75`;
  let h = Math.round((dimensions.height * viewportWidth) / dimensions.width);
  let w = viewportWidth;
  let paramsSet = {
    '1x': `w=${viewportWidth}&h=${h}&q=75`,
    '1.5x': `w=${Math.min(viewportWidth * 1.5, 4000)}&h=${h * 1.5}&q=75`,
    '2x': `w=${Math.min(viewportWidth * 2, 4000)}&h=${h * 2}&q=75`,
  };
  if (height) {
    h = height;
    w = Math.round((dimensions.width * h) / dimensions.height);
    params = `w=${w}&h=${h}&fit=crop&q=75&f=faces`;
    paramsSet = {
      '1x': `w=${w}&h=${h}&fit=crop&q=75&f=faces`,
      '1.5x': `w=${Math.floor(Math.min(w * 1.5, 4000))}&h=${h * 1.5}&fit=crop&q=75&f=faces`,
      '2x': `w=${Math.floor(Math.min(w * 2, 4000))}&h=${h * 2}&fit=crop&q=75&f=faces`,
    };
  }
  return {
    base64,
    height: h,
    src: `${url}?${params}`,
    srcSet: `${url}?${paramsSet['1x']} 1x,
${url}?${paramsSet['1.5x']} 1.5x,
${url}?${paramsSet['2x']} 2x,`,
    width: w,
  };
};

/**
 * Gets image resolutions based on exact dimensions
 * @param {object} data arguments
 * @returns {object} resolutions object
 */
export const getExactResolutions = ({
  image = {},
  width,
  height,
  noCrop,
}) => {
  const {
    file = {
      url: '',
      details: {
        image: {
          width: 0,
          height: 0,
        },
      },
    },
    resolutions = {
      base64: '',
    },
  } = image;
  const { url = '' } = file;
  const { base64 = '' } = resolutions;
  let cropString = '&fit=crop&f=face';
  if (noCrop) cropString = '';
  const params = `w=${width}&h=${height}&q=75${cropString}`;
  const paramsSet = {
    '1x': `w=${width}&h=${height}&q=75${cropString}`,
    '1.5x': `w=${Math.floor(Math.min(width * 1.5, 4000))}&h=${Math.floor(
      Math.min(height * 1.5, 4000),
    )}&q=75${cropString}`,
    '2x': `w=${Math.floor(Math.min(width * 2, 4000))}&h=${Math.floor(Math.min(height * 2, 4000))}&q=75${cropString}`,
  };
  return {
    base64,
    height,
    src: `${url}?${params}`,
    srcSet: `${url}?${paramsSet['1x']} 1x,
${url}?${paramsSet['1.5x']} 1.5x,
${url}?${paramsSet['2x']} 2x,`,
    width,
  };
};



// WEBPACK FOOTER //
// ./src/utils/webp.js